/**
 * Aggregates multiple API error toasts into a single notification.
 *
 * When APIs are under pressure, many requests may fail within a short window.
 * Instead of showing one toast per failure, this utility collects errors over
 * a fixed window (500 ms from the first error) and shows a single, clean toast.
 * Full details are logged to the console for debugging.
 */

import toast from 'react-hot-toast';

const AGGREGATION_WINDOW_MS = 500;
const MAX_PENDING_ERRORS = 50;
const ERROR_TOAST_ID = 'aggregated-api-error';

interface PendingError {
  operationName: string;
  message: string;
}

let pendingErrors: PendingError[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function flush() {
  if (pendingErrors.length === 0) return;

  const errors = [...pendingErrors];
  pendingErrors = [];
  flushTimer = null;

  // Log full details to the console for debugging
  if (errors.length > 1) {
    const operations = [...new Set(errors.map((e) => e.operationName).filter(Boolean))];
    const uniqueMessages = [...new Set(errors.map((e) => e.message))];
    console.warn(
      `[errorToastAggregator] ${errors.length} requests failed within aggregation window`,
      { operations, uniqueMessages },
    );
  }

  // Show the first error's message — keep it simple and actionable for the user
  toast.error(errors[0].message, { id: ERROR_TOAST_ID });
}

/**
 * Queue an error for display. Errors arriving within `AGGREGATION_WINDOW_MS`
 * of the first queued error are combined into a single toast. The window is
 * fixed (not sliding) so feedback is guaranteed within 500 ms.
 */
export function queueErrorToast(operationName: string, message: string): void {
  if (pendingErrors.length < MAX_PENDING_ERRORS) {
    pendingErrors.push({ operationName, message });
  }

  // Fixed window: only start the timer on the first error.
  // Subsequent errors within the window are collected but don't extend it.
  if (flushTimer === null) {
    flushTimer = setTimeout(flush, AGGREGATION_WINDOW_MS);
  }
}
